/**
 * Payment API Layer
 * 
 * Handles all payment-related API calls including:
 * - Setup intents for new subscriptions
 * - Payment method management
 * - Subscription management (activate, cancel, update)
 */

import { apiPost, apiGet, apiPut } from './client';
import { PaymentCompleteResponse } from '../types';

// ============================================
// Request Interfaces
// ============================================

export interface PaymentSetupIntentRequest {
  reimbursementRef: string;
  email: string;
  option: number;
}

export interface PaymentCompleteRequest {
  reimbursementRef: string;
  paymentMethodId: string;
}

export interface CancelSubscriptionRequest {
  reason?: string;
  feedback?: string;
}

export interface ReactivateSubscriptionRequest {
  paymentMethodId: string;
}

export interface UpdatePaymentMethodRequest {
  paymentMethodId: string;
}

// ============================================
// Response Interfaces
// ============================================

export interface SetupIntentApiResponse {
  token: string;
  amount: number;
  discountedAmount?: number;
}

export interface SubscriptionSetupIntentResponse {
  token: string;
  amount: number;
}

// ============================================
// Payment API Functions
// ============================================

/**
 * Initialize payment and get client secret for Stripe
 * Used for new reimbursement subscriptions
 */
export const paymentSetUpIntent = async (
  body: PaymentSetupIntentRequest
): Promise<SetupIntentApiResponse> => {
  return apiPost<SetupIntentApiResponse>('api/v1/payments/reimbursement/setup-intent', body);
};

/**
 * Complete the payment after Stripe card setup
 */
export const completePaymentMethod = async (
  body: PaymentCompleteRequest
): Promise<PaymentCompleteResponse> => {
  return apiPost<PaymentCompleteResponse>('api/v1/payments/reimbursement/complete', body);
};

/**
 * Get setup intent for subscription management (reactivate or update payment method)
 * @param paymentMethodUpdate - true for updating existing method, false for reactivation
 */
export const paymentSetUpIntentSubscription = async (
  paymentMethodUpdate: boolean
): Promise<SubscriptionSetupIntentResponse> => {
  return apiGet<SubscriptionSetupIntentResponse>(
    `api/v1/reimbursement-accounts/me/subscriptions/setup-intent?paymentMethodUpdate=${paymentMethodUpdate}`
  );
};

/**
 * Cancel user's subscription
 */
export const cancelSubscription = async (
  ref: string,
  body: CancelSubscriptionRequest
): Promise<void> => {
  return apiPut<void>(`api/v1/reimbursement-accounts/me/subscriptions/${ref}/cancel`, body);
};

/**
 * Reactivate a cancelled subscription
 */
export const reActivateSubscription = async (
  body: ReactivateSubscriptionRequest
): Promise<void> => {
  return apiPost<void>('api/v1/reimbursement-accounts/me/subscriptions/activate', body);
};

/**
 * Update the payment method for an existing subscription
 */
export const updatePaymentMethod = async (
  body: UpdatePaymentMethodRequest
): Promise<void> => {
  return apiPut<void>('api/v1/reimbursement-accounts/me/payment-methods', body);
};

/**
 * Initialize payment (legacy endpoint)
 */
export const createClientSecret = async (body: {
  reimbursementRef: string;
  email: string;
}): Promise<{ clientSecret: string }> => {
  return apiPost<{ clientSecret: string }>('api/v1/payments/reimbursement/init', body);
};
